/**
 * API client para o app `pedidos/` do backend (Fase 4 turnos 2 e 3).
 *
 * Endpoints (sem trailing slash):
 *   GET    /api/pedidos                              list (filtros: situacao/cliente/vendedor/prioridade/orcamento)
 *   GET    /api/pedidos/{id}
 *   PATCH  /api/pedidos/{id}                         editar meta (observacoes/prioridade)
 *   DELETE /api/pedidos/{id}                         (pedido:delete + MFA)
 *   POST   /api/pedidos/{id}/iniciar-producao
 *   POST   /api/pedidos/{id}/iniciar-expedicao
 *   POST   /api/pedidos/{id}/marcar-entregue
 *   POST   /api/pedidos/{id}/cancelar               body: {motivo}
 *   POST   /api/pedidos/{id}/iniciar-revisao        body: {motivo} (ADR-0015)
 *   POST   /api/pedidos/{id}/finalizar-revisao
 *   POST   /api/pedidos/{id}/cancelar-revisao
 *
 *   GET    /api/pedido-revisoes?pedido={id}         historico read-only
 *
 *   CRUD: /api/pedido-ambientes (gate via revisao)
 *   CRUD: /api/pedido-itens (gate via revisao)
 *
 * POST direto em /api/pedidos retorna 405; criar pedido = converter
 * orcamento (use services/orcamentos.js -> converterEmPedido).
 */

const PREFIXO_PED = "/api/pedidos";
const PREFIXO_REV = "/api/pedido-revisoes";
const PREFIXO_AMB = "/api/pedido-ambientes";
const PREFIXO_ITEM = "/api/pedido-itens";

// ---------- Pedido ----------

export function listarPedidos(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.busca) usp.set("search", params.busca);
  if (params.situacao) usp.set("situacao", params.situacao);
  if (params.cliente) usp.set("cliente", params.cliente);
  if (params.vendedor) usp.set("vendedor", params.vendedor);
  if (params.prioridade) usp.set("prioridade", params.prioridade);
  if (params.orcamento) usp.set("orcamento", params.orcamento);
  if (params.ordering) usp.set("ordering", params.ordering);
  if (params.page) usp.set("page", params.page);
  if (params.pageSize) usp.set("page_size", params.pageSize);
  const qs = usp.toString();
  return request(`${PREFIXO_PED}${qs ? `?${qs}` : ""}`);
}

export function obterPedido(request, id) {
  return request(`${PREFIXO_PED}/${id}`);
}

// Cockpit do pedido (Detalhe — Fase 2): cabecalho + KPIs + steps +
// timeline de estacoes + OPs + eventos. GET /api/pedidos/{id}/cockpit.
export function obterCockpitPedido(request, id, options = {}) {
  return request(`${PREFIXO_PED}/${id}/cockpit`, options);
}

// Cronograma de pagamento por evento (Tarefa B2/F2): parcelas da condicao
// de pagamento do pedido com datas previstas das datas reais do processo.
// GET /api/pedidos/{id}/cronograma-pagamento — pedido sem condicao retorna
// {condicao_pagamento: null, parcelas: []}.
export function obterCronogramaPagamento(request, id, options = {}) {
  return request(`${PREFIXO_PED}/${id}/cronograma-pagamento`, options);
}

// Faixa de status da Lista (mockup Fase 2): contagem por situacao.
// ?vendedor= opcional (espelha o segmento "Meus").
export function contagemStatusPedidos(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.vendedor) usp.set("vendedor", params.vendedor);
  const qs = usp.toString();
  return request(`${PREFIXO_PED}/contagem-status${qs ? `?${qs}` : ""}`);
}

// ---------- Acoes em lote (bulkbar) ----------

export function marcarEntregueLote(request, ids) {
  return request(`${PREFIXO_PED}/marcar-entregue-lote`, {
    method: "POST",
    body: JSON.stringify({ ids }),
  });
}

export function cancelarLote(request, ids, motivo) {
  return request(`${PREFIXO_PED}/cancelar-lote`, {
    method: "POST",
    body: JSON.stringify({ ids, motivo }),
  });
}

export function atribuirVendedorLote(request, ids, vendedor) {
  return request(`${PREFIXO_PED}/atribuir-vendedor-lote`, {
    method: "POST",
    body: JSON.stringify({ ids, vendedor: vendedor || null }),
  });
}

export function atualizarPedido(request, id, dados) {
  return request(`${PREFIXO_PED}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}

export function iniciarProducao(request, id) {
  return request(`${PREFIXO_PED}/${id}/iniciar-producao`, { method: "POST" });
}

export function iniciarExpedicao(request, id) {
  return request(`${PREFIXO_PED}/${id}/iniciar-expedicao`, { method: "POST" });
}

export function marcarEntregue(request, id) {
  return request(`${PREFIXO_PED}/${id}/marcar-entregue`, { method: "POST" });
}

export function cancelarPedido(request, id, motivo, { cancelarOps = false } = {}) {
  // `cancelar_ops` (auditoria 2026-08-07, ALTA-10): sem ele o chão de fábrica
  // continuava produzindo um pedido morto — o backend aceita o flag desde a
  // Fase 8.5 e nenhuma tela o enviava.
  return request(`${PREFIXO_PED}/${id}/cancelar`, {
    method: "POST",
    body: JSON.stringify({ motivo, cancelar_ops: cancelarOps }),
  });
}

// Fase 8.1 — gera N OrdemProducao a partir do BOM CAD do pedido (ADR-0022).
// Backend retorna 201 com { ...pedido, ops_geradas: [...] }.
// Erros possiveis:
//   400 com {detalhe} — pedido sem BOM ou fora de RECEBIDO
//   409 com {detalhe, codigo:"ops_ja_geradas"} — idempotencia
//   403 — sem permissao ordem_producao:gerar_ops
/**
 * M-PED-6: `liberar` decide se a peca vai direto para o chao (true) ou
 * espera a conferencia do PCP em RASCUNHO (false). O backend aceita o
 * parametro desde a Fase 8.4; o frontend nunca o enviava, entao toda OP
 * nascia liberada e a etapa de conferencia nao existia na pratica.
 */
export function gerarOpsDoPedido(request, id, { liberar = true } = {}) {
  return request(`${PREFIXO_PED}/${id}/gerar-ops`, {
    method: "POST",
    body: JSON.stringify({ liberar }),
  });
}

/** Libera para o chao todas as OPs que ficaram em RASCUNHO (M-PED-6). */
export function liberarOpsDoPedido(request, id) {
  return request(`${PREFIXO_PED}/${id}/liberar-ops`, { method: "POST" });
}

// ---------- Revisao (ADR-0015) ----------

export function iniciarRevisao(request, id, motivo) {
  return request(`${PREFIXO_PED}/${id}/iniciar-revisao`, {
    method: "POST",
    body: JSON.stringify({ motivo }),
  });
}

export function finalizarRevisao(request, id) {
  return request(`${PREFIXO_PED}/${id}/finalizar-revisao`, { method: "POST" });
}

export function cancelarRevisao(request, id) {
  return request(`${PREFIXO_PED}/${id}/cancelar-revisao`, { method: "POST" });
}

export function listarRevisoes(request, pedidoId) {
  return request(`${PREFIXO_REV}?pedido=${pedidoId}`);
}

// ---------- PedidoAmbiente / PedidoItem (gate: revisao_em_andamento OR RECEBIDO) ----------

export function listarPedidoAmbientes(request, pedidoId) {
  return request(`${PREFIXO_AMB}?pedido=${pedidoId}`);
}

export function criarPedidoAmbiente(request, dados) {
  return request(PREFIXO_AMB, {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

export function atualizarPedidoAmbiente(request, id, dados) {
  return request(`${PREFIXO_AMB}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}

export function deletarPedidoAmbiente(request, id) {
  return request(`${PREFIXO_AMB}/${id}`, { method: "DELETE" });
}

export function listarPedidoItens(request, ambienteId) {
  return request(`${PREFIXO_ITEM}?ambiente=${ambienteId}`);
}

export function criarPedidoItem(request, dados) {
  return request(PREFIXO_ITEM, {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

export function atualizarPedidoItem(request, id, dados) {
  return request(`${PREFIXO_ITEM}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}

export function deletarPedidoItem(request, id) {
  return request(`${PREFIXO_ITEM}/${id}`, { method: "DELETE" });
}
